/**
 * ticket-inapp.stencil — the third of the package's shipped in-app card
 * variants (see ticket-inapp.card.ts for the variant dispatcher). One solid
 * vivid-orange ground (ticket.palette.ts's `stencil` ground, painted through
 * ticket-inapp.palette.ts's `.tkt-stencil-ground`) under white ink: a
 * display wordmark spans the full content width at the top and the brand
 * reads small at the foot, two label/value strips carry the date/venue/
 * type/number facts (the first column left-aligned, the second right-
 * aligned against the card's own right edge — unified with Google Wallet's
 * own fixed `endItem` alignment, which no field on the pass composers can
 * move; the card and Apple's own pass follow it rather than fight it, so
 * a value's column edge means the same thing on every surface), and a
 * rounded black frame holding a white
 * QR plate with its caption inside the frame sits between them — a
 * deliberate, reviewed departure from the reference photo's own flat,
 * unframed QR square (see that section's own header for the full note).
 *
 * NOTHING ON THIS CARD CAN CLIP, FOR ANY INPUT — the structural guarantee
 * every text slot on this card shares, not a per-string tuning:
 *
 *   - The hero wordmark and the footer brand wordmark
 *     (ticket-inapp.stencil-wordmark.ts, `<tkt-fitted-wordmark>`) measure
 *     their own content's natural rendered width and apply one CSS
 *     `scaleX()` correction — the hero always fills its box exactly (any
 *     length, the reference's own "spans edge to edge" look), the footer
 *     only ever shrinks a natural render that would overflow, never
 *     stretches a short brand name up.
 *   - Every value (date, venue, ticket type, ticket number —
 *     ticket-inapp.stencil-fit-text.ts, `<tkt-fit-text>`) shrinks to fit its
 *     column on one line down to a legibility floor, and only past that
 *     floor allows a wrap — never a truncation, never `line-clamp`, never
 *     an ellipsis.
 *   - Every value slot's wrapper below reserves a fixed two-line
 *     `min-height` UNCONDITIONALLY, whether or not this render's own content
 *     needs the second line, so the QR block and footer never shift
 *     position depending on how long any one value happens to be.
 *   - The card's own `overflow-hidden` is a backstop only — every mechanism
 *     above is what actually keeps content inside its box; nothing on this
 *     card is expected to reach that edge.
 *
 * THE COLUMN SPLIT IS SIZED FOR REAL VALUES, NOT THE REFERENCE PHOTO'S OWN
 * LABEL POSITIONS: the reference is a label-only mockup with no values
 * under "VENUE"/"TICKET NR", so its own ~74%-left-edge right column cannot
 * be reproduced once real data exists — a two-word venue name of ordinary
 * length cannot render on one line in the ~18%-wide column that position
 * implies.
 * `grid-cols-[1.3fr_1fr]` below is sized against the realistic longest
 * value in each column instead (a formatted date range on the left, a venue
 * name on the right) — the right column's own generous `1fr` is what makes
 * right-aligning safe: unlike the reference's ~18% slot, a value here never
 * hugs a boundary its own longest realistic content would clip against.
 *
 * THE HERO WORDMARK IS AN UPLOADED IMAGE WHEN THE EVENT HAS ONE
 * (`TicketTemplateEvent.logoUrl`, ticket.props.ts) — an event that carries
 * its own designed mark (the reference design's own "Hallen 07" wordmark
 * image) renders that image at the same left edge and height budget the
 * generated form spends. Absent an upload, the card falls back to the
 * generated wordmark above — and so does an upload whose url does not
 * resolve, which is `<tkt-mark>`'s job (ticket-inapp.stencil-mark.ts): both
 * of this card's image slots go through it, so neither can ever paint the
 * browser's broken-image glyph onto a ticket.
 *
 * EVERY GAP ON THIS CARD IS A DELIBERATE BUDGET AGAINST THE FIXED 576px
 * DESIGN HEIGHT, not an incidental default: the wordmark, the two label
 * rows (at their unconditionally-reserved two-line height) and the footer
 * take fixed budgets whose sum — plus the padding and the gaps between them
 * — fits inside 576px, and the QR block spends the whole of what is left
 * (`flex-1`, `min-h-0` load-bearing the same way it is on editorial's own
 * grid items), squared and capped at the content width so it can never
 * reach the card's gutters. The card's own
 * `aspect-[2/3]` is the shared design contract every variant, the PDF
 * render path (`TICKET_FILE_DESIGN_WIDTH`) and every scaled/thumbnail
 * rendering (`renderTicketCardScaled`) already pin, so it is never adjusted
 * per variant; the reference photo's own card is a different (4:5) ratio,
 * and this card's extra vertical room over that ratio is spent on the gaps
 * between sections.
 *
 * BRAND WORDMARK SPLITS ON THE FIRST SPACE: `props.brand.name`'s first word
 * renders as the big display wordmark, the remainder (if any) as a small
 * caption beneath it — a single-word brand renders the wordmark alone with
 * no second line. The caption above it, "Produced by"
 * (`documents.ticket.producedByLabel`), is the platform's own fixed copy: a
 * ticket's issuer is always its own brand, so this line never names a
 * different deployment. Neither wordmark carries the display face's tight
 * default tracking: `WORDMARK_TRACKING_EM`
 * (ticket-inapp.stencil-wordmark.ts) owns that reasoning for both of them.
 *
 * THE FOOTER IS THE OPERATOR'S OWN UPLOADED MARK WHEN THE WORKSPACE HAS ONE
 * (`TicketTemplateBrand.logoUrl`, ticket.props.ts), on the same terms the
 * hero takes the event's: the image replaces the GENERATED wordmark, the
 * "Produced by" caption above it is unchanged, and an absent upload falls
 * back to the generated form. The tagline does NOT survive that swap — a
 * designed brand mark already spells the operator's whole name, so printing
 * the name's remainder under it says the same word twice. The footer's
 * height is identical either way: the mark is given exactly the envelope the
 * generated wordmark plus its tagline would have spent, so which form the
 * footer draws never moves the QR's surplus.
 *
 * THE MARK TAKES THE CARD'S OWN INK, SO NO SECOND ASSET IS CHOSEN BETWEEN.
 * The image is drawn as its own silhouette in the same white this card sets
 * every other glyph in (the filter pair on `.tkt-stencil-brand-logo`,
 * ticket-inapp.styles.ts). A workspace stores one mark and the deployment
 * derives a whitened variant beside it for dark wallet grounds; a surface
 * that hands a real file to Apple or Google has to pick between the two, and
 * this one does not, because it controls the rendering. What it needs of the
 * asset is an alpha channel — see that rule for the jpeg case.
 */

import { formatDateRange } from "@mhmo91/rx";
import { translate } from "@momo/i18n";
import { html, type TemplateResult } from "lit";
import { when } from "lit/directives/when.js";
import type { TicketTemplateProps } from "./ticket.props.ts";
import { ticketCardGrounds } from "./ticket-inapp.palette.ts";
import { renderQrCredentialCode } from "./ticket-inapp.qr-hero.ts";
import { renderFitValue } from "./ticket-inapp.stencil-fit-text.ts";
import { renderUploadedMark } from "./ticket-inapp.stencil-mark.ts";
import {
	renderWordmarkFooter,
	renderWordmarkFullWidth,
	STENCIL_CONTENT_WIDTH_PX,
	stencilWordmarkTier,
} from "./ticket-inapp.stencil-wordmark.ts";
import {
	STENCIL_FOOTER_GAP_PX,
	STENCIL_TAGLINE_LINE_PX,
	ticketCardStyles,
} from "./ticket-inapp.styles.ts";

/** The card's fixed content width — 384 (the design width every card in the
 *  package shares) minus `px-6` (24px) on both sides — the exact span both
 *  the hero wordmark's fit and the label rows resolve against. Declared with
 *  the wordmark element the wallet band shares, so both surfaces resolve the
 *  hero against one number. */
const CONTENT_WIDTH_PX = STENCIL_CONTENT_WIDTH_PX;

/** The two label-row columns' own pixel widths — `grid-cols-[1.3fr_1fr]`
 *  worked out against CONTENT_WIDTH_PX and the row's `gap-4` (16px), so
 *  `renderFitValue`'s own width target matches the column it actually
 *  renders inside. See this file's header for why this split (not the
 *  reference photo's own ~74%) is what real values need. */
const LABEL_ROW_GAP_PX = 16;
const LEFT_COLUMN_PX = ((CONTENT_WIDTH_PX - LABEL_ROW_GAP_PX) * 1.3) / 2.3;
const RIGHT_COLUMN_PX = ((CONTENT_WIDTH_PX - LABEL_ROW_GAP_PX) * 1) / 2.3;

/** Two line-heights at the value font-size, reserved unconditionally on
 *  every value slot regardless of whether this render's own content needs
 *  the second line — see this file's header for why the reservation is
 *  unconditional. Both numbers sit at the tight end of legible (13px body
 *  text, 1.2 line-height) rather than schmancy's own `body/sm` default
 *  (14px/1.3) specifically to keep this reservation, doubled, inside the
 *  card's fixed 576px vertical budget alongside every other section. */
const VALUE_FONT_PX = 13;
const VALUE_MIN_HEIGHT_PX = VALUE_FONT_PX * 1.2 * 2;

export function renderTicketInAppStencil(
	props: TicketTemplateProps,
): TemplateResult {
	const { locale, brand, event, ticket } = props;
	const dateLabel = translate(locale, "documents.ticket.dateLabel");
	const venueLabel = translate(locale, "documents.ticket.venueLabel");
	const ticketTypeLabel = translate(locale, "documents.ticket.ticketTypeLabel");
	const ticketNumberLabel = translate(
		locale,
		"documents.ticket.ticketNumberLabel",
	);
	const producedByLabel = translate(locale, "documents.ticket.producedByLabel");
	const scheduleValue = formatDateRange(event.start, event.end, {
		locale: locale === "de" ? "de-DE" : "en-US",
		month: "long",
		timeZone: event.timezone,
	});
	const [brandLead, ...brandRestWords] = brand.name.trim().split(/\s+/);
	const brandTagline = brandRestWords.join(" ");
	const qrHint = translate(locale, "documents.ticket.qrHint");
	// The ladder itself lives with the wordmark element it sizes, because the
	// wallet band renders this same hero region and must take the same tier.
	const { fontSizePx: wordmarkFontPx, boxPx: wordmarkBoxPx } =
		stencilWordmarkTier(event.name);
	// A third of the hero's own tier at each rung of the same ladder
	// (STENCIL_WORDMARK_TIERS' 50/72/104 at the same length thresholds), so the
	// card's two display wordmarks stay in fixed proportion to each other —
	// which is why this reads the EVENT name's length rather than the brand's.
	const brandWordmarkFontPx =
		event.name.length > 28 ? 17 : event.name.length > 14 ? 24 : 34;
	const brandWordmarkBoxPx = brandWordmarkFontPx * 1.1;
	// What an uploaded mark is given: the generated wordmark's own box, plus
	// the tagline line and the gap above it wherever the generated form would
	// have drawn one. Spending the whole envelope rather than the wordmark's
	// box alone is what holds the footer — and so the QR's surplus — at exactly
	// the same height whichever form this footer draws.
	const brandLogoHeightPx =
		brandWordmarkBoxPx +
		(brandTagline ? STENCIL_FOOTER_GAP_PX + STENCIL_TAGLINE_LINE_PX : 0);

	// Each of the card's two identity slots is a generated display wordmark,
	// and each is replaced by an uploaded image where the operator has one.
	// The generated form is built once and used twice per slot — as the
	// no-upload rendering, and as what an upload that cannot be fetched falls
	// back to (renderUploadedMark, ticket-inapp.stencil-mark.ts) — so the two
	// paths can never drift into different type.
	const heroWordmark = html`
    <div class="tkt-card-display tkt-stencil-wordmark" style="width:${CONTENT_WIDTH_PX}px">
      ${renderWordmarkFullWidth(event.name, {
				widthPx: CONTENT_WIDTH_PX,
				heightPx: wordmarkBoxPx,
				fontSizePx: wordmarkFontPx,
			})}
    </div>
  `;
	const brandWordmark = html`
    <div class="tkt-card-display" style="width:${CONTENT_WIDTH_PX}px">
      ${renderWordmarkFooter(brandLead ?? "", {
				maxWidthPx: CONTENT_WIDTH_PX,
				heightPx: brandWordmarkBoxPx,
				fontSizePx: brandWordmarkFontPx,
			})}
    </div>
    ${when(
			brandTagline,
			() => html`
        <p class="tkt-stencil-tagline">
          ${brandTagline}
        </p>
      `,
		)}
  `;

	return html`
    ${ticketCardGrounds}${ticketCardStyles}
    <div class="tkt-card tkt-stencil-ground tkt-stencil-card tkt-card-text">
      <!-- The uploaded event wordmark, preferred over the generated one
           (ticket.props.ts's own TicketTemplateEvent.logoUrl contract) —
           left-aligned and height-bounded to the same vertical budget the
           generated wordmark's tallest tier spends, so an image and generated
           text never shift the layout below it. The bounding rules, and why
           the image opts out of the flex column's own stretch, live with
           .tkt-stencil-logo in ticket-inapp.styles.ts. -->
      ${when(
				event.logoUrl,
				(logoUrl) =>
					renderUploadedMark(logoUrl, {
						alt: event.name,
						markClass: "tkt-stencil-logo",
						markStyle: "",
						fallback: heroWordmark,
					}),
				() => heroWordmark,
			)}

      <div class="tkt-stencil-rows">
        <div class="tkt-stencil-row">
          <div class="tkt-stencil-cell">
            <p class="tkt-stencil-label">
              ${dateLabel}
            </p>
            <div class="tkt-stencil-value" style="min-height:${VALUE_MIN_HEIGHT_PX}px">
              ${renderFitValue(scheduleValue, { widthPx: LEFT_COLUMN_PX, fontSizePx: VALUE_FONT_PX })}
            </div>
          </div>
          <div class="tkt-stencil-cell tkt-align-end">
            <p class="tkt-stencil-label">
              ${venueLabel}
            </p>
            <div class="tkt-stencil-value" style="min-height:${VALUE_MIN_HEIGHT_PX}px">
              ${renderFitValue(event.venue.name, { widthPx: RIGHT_COLUMN_PX, fontSizePx: VALUE_FONT_PX })}
            </div>
          </div>
        </div>
        <div class="tkt-stencil-row">
          <div class="tkt-stencil-cell">
            <p class="tkt-stencil-label">
              ${ticketTypeLabel}
            </p>
            <div class="tkt-stencil-value" style="min-height:${VALUE_MIN_HEIGHT_PX}px">
              ${renderFitValue(ticket.typeName, { widthPx: LEFT_COLUMN_PX, fontSizePx: VALUE_FONT_PX })}
            </div>
          </div>
          <div class="tkt-stencil-cell tkt-align-end">
            <p class="tkt-stencil-label">
              ${ticketNumberLabel}
            </p>
            <div class="tkt-stencil-value tkt-stencil-value--mono" style="min-height:${VALUE_MIN_HEIGHT_PX}px">
              ${renderFitValue(ticket.displayId, { widthPx: RIGHT_COLUMN_PX, fontSizePx: VALUE_FONT_PX })}
            </div>
          </div>
        </div>
      </div>

      <!-- THE BLACK FRAME IS A DELIBERATE, USER-APPROVED DEPARTURE FROM THE
           REFERENCE PHOTO, NOT AN OVERSIGHT TO "FIX" TOWARD IT: the
           reference's own QR block is a flat, unframed square; this card's
           black-frame / white-plate / caption-inside nesting was reviewed
           against that reference and kept on its own merits. A fidelity
           pass resizes this assembly — never strips the frame itself back
           to a plain square.
           THE CREDENTIAL IS THE CARD'S SURPLUS, AND ITS SIZE IS STATED
           NOWHERE: the wordmark, the two label rows and the footer take
           their own fixed budgets first, and whatever the fixed 576px
           design height has left is exactly what this block spends — the
           smaller of that surplus and the content width, resolved by the
           slot / frame / plate chain in ticket-inapp.styles.ts. The one number
           this composition supplies is the width bound below: the card's
           own content width, so the square can never reach its gutters. A
           short event name (the tallest wordmark tier) leaves a smaller
           square than a long one, and both spend everything left rather
           than centring a fixed size in slack.
           The code itself comes from renderQrCredentialCode
           (ticket-inapp.qr-hero.ts), the one module that encodes this
           package's credential — its ink, its error-correction level and
           its quiet-zone decision are the credential's own policy, not this
           composition's to restate. That function bakes no quiet zone into
           the SVG; the plate's own padding supplies it as real layout
           space. -->
      <div class="tkt-stencil-qr-slot">
        <div class="tkt-stencil-qr-frame" style="--tkt-stencil-qr-max-width:${CONTENT_WIDTH_PX}px">
          <div
            role="img"
            aria-label=${qrHint}
            class="tkt-stencil-qr-plate"
          >
            ${renderQrCredentialCode(ticket.qrToken)}
          </div>
          <p class="tkt-stencil-qr-caption">
            ${qrHint}
          </p>
        </div>
      </div>

      <div class="tkt-stencil-footer">
        <p class="tkt-stencil-footer-label">
          ${producedByLabel}
        </p>
        <!-- The operator's own uploaded mark, preferred over the generated
             wordmark and drawn with no tagline under it — see this file's
             header for both. An img element rather than a masked box, on two
             counts: it carries the intrinsic aspect ratio that lets object-fit
             resolve any upload's shape with no ratio stated anywhere, and it is
             the only form the file render path's own readiness gate can see
             (pdf-render walks document.images and awaits each decode, which no
             CSS mask resource joins). The height is the whole envelope the
             generated form would have spent; the width cap, the no-crop rule
             and the ink live with the class in ticket-inapp.styles.ts. -->
        ${when(
					brand.logoUrl,
					(logoUrl) =>
						renderUploadedMark(logoUrl, {
							alt: brand.name,
							markClass: "tkt-stencil-brand-logo",
							markStyle: `height:${brandLogoHeightPx}px`,
							fallback: brandWordmark,
						}),
					() => brandWordmark,
				)}
      </div>
    </div>
  `;
}
